import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useKbArticle } from '@/queries/kb'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { formatDate } from '@/lib/dates'

function KbInlineImage({ src, alt }: { src?: string; alt?: string }) {
  const [resolved, setResolved] = useState<string | null>(null)
  const isApi = typeof src === 'string' && src.includes('/kb-articles/images/')

  if (!isApi) {
    return <img src={src} alt={alt ?? ''} className="rounded border border-border max-h-[480px]" />
  }
  if (!resolved) {
    fetch(`${src}?json=1`, { credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.url) setResolved(j.url) })
      .catch(() => undefined)
    return <span className="text-xs text-muted-foreground">{alt ?? 'image'} (loading…)</span>
  }
  return <img src={resolved} alt={alt ?? ''} className="rounded border border-border max-h-[480px]" />
}

export function KbArticleView({ slug }: { slug: string }) {
  const { data, isLoading, error } = useKbArticle(slug)
  const { user } = useAuth0()
  const isAdmin = Boolean((user as Record<string, unknown> | undefined)?.['https://app.etainabl.com/isAdmin'])

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-3xl p-6">
        <p className="text-sm text-muted-foreground mb-4">Article not found.</p>
        <Link to="/kb"><Button variant="outline" size="sm"><ArrowLeft className="mr-1.5 h-4 w-4" />Back to Knowledgebase</Button></Link>
      </div>
    )
  }

  return (
    <article className="container mx-auto max-w-3xl p-6">
      <Link to="/kb" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-3.5 w-3.5" />
        Knowledgebase
      </Link>

      {!data.isPublished && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 mb-4">
          This article is a draft. Only admins can see it.
        </div>
      )}

      <header className="mb-6 space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">{data.title}</h1>
        {data.subtitle && <p className="text-lg text-muted-foreground">{data.subtitle}</p>}
        <div className="text-xs text-muted-foreground">
          Updated {formatDate(data.updatedAt)}
        </div>
      </header>

      <div className="prose prose-sm sm:prose max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ img: KbInlineImage as never }}>{data.content || '*No content yet.*'}</ReactMarkdown>
      </div>

      {isAdmin && (
        <div className="mt-8 pt-6 border-t">
          <Link to={`/admin/kb/${data._id}` as never}>
            <Button size="sm" variant="outline">Edit article</Button>
          </Link>
        </div>
      )}
    </article>
  )
}
